import {
  createLink,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import type { ReactNode } from "react";

import {
  Icon,
  MobileNav,
  NavItem,
  SideRail,
  ScrollArea,
  StoreSwitcher,
  type IconName,
} from "@go-novus/ui";

import { storeConnectionAtom } from "#/features/store-connection";
import * as m from "#/paraglide/messages";
import logoUrl from "#/shared/assets/logo-color.svg";
import logoDarkUrl from "#/shared/assets/logo-dark.svg";
import { openShopifyAdmin } from "#/shared/lib/shopify-admin";
import { useTheme } from "#/shared/lib/theme";
import { useShopifyNavigation } from "../hooks/use-shopify-navigation";

/**
 * NavItem 自己声明了 href，有 href 就渲染成 <a> 并转发 ref，
 * 所以能直接被包成路由链接：createLink 只负责把 to 翻译成 href 和客户端 onClick。
 */
const NavItemLink = createLink(NavItem);

interface NavIconProps {
  active: boolean;
  name: IconName;
}

/** 选中行的图标用品牌粉，其余跟随行的文字色。 */
function NavIcon({ active, name }: NavIconProps) {
  return (
    <span className={active ? "text-[var(--brand-mark)]" : "text-current"}>
      <Icon name={name} size={16} />
    </span>
  );
}

export interface ConsoleShellProps {
  children?: ReactNode;
}

/**
 * 控制台外壳：左侧主导航 + 内容区。全屏页面不挂它。
 * 窄屏（<768px）把侧栏换成底部的 MobileNav：224px 的侧栏在手机上会把内容区
 * 挤到 151px，整页横向溢出。换法见 docs/agents/ui-style.md 的 Layout 一节。
 */
export function ConsoleShell({ children }: ConsoleShellProps) {
  useShopifyNavigation();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const navigate = useNavigate();
  const tenant = useAtomValue(storeConnectionAtom).data;
  const { theme, preference, setTheme } = useTheme();
  const isDark = theme === "dark";
  const navItems = [
    { icon: "home", label: m.console_nav_home(), to: "/" },
    { icon: "inbox", label: m.console_nav_inbox(), to: "/inbox" },
    { icon: "knowledge", label: m.console_nav_knowledge(), to: "/knowledge" },
    {
      icon: "playground",
      label: m.console_nav_playground(),
      to: "/playground",
    },
  ] as const;
  const settingsItem = {
    icon: "settings",
    label: m.console_nav_settings(),
    to: "/settings",
  } as const;
  const allItems = [...navItems, settingsItem];
  const activeItem = allItems.find((item) => item.to === pathname);
  // 侧栏和窄屏顶栏摆的是同一个店铺块，两边都靠它认出人在哪家店。
  const storeSwitcher =
    tenant?.status !== "ready" ? null : (
      <StoreSwitcher
        items={[
          {
            label: m.console_theme_light(),
            icon: <Icon name="sun" />,
            selected: preference === "light",
            onSelect: () => {
              setTheme("light");
            },
          },
          {
            label: m.console_theme_dark(),
            icon: <Icon name="moon" />,
            selected: preference === "dark",
            onSelect: () => {
              setTheme("dark");
            },
          },
          {
            label: m.console_theme_system(),
            icon: <Icon name="monitor" />,
            selected: preference === "system",
            onSelect: () => {
              setTheme("system");
            },
          },
          {
            label: m.console_store_open_in_shopify(),
            icon: <Icon name="shopify" />,
            separatorBefore: true,
            onSelect: () => {
              openShopifyAdmin(tenant.shopDomain);
            },
          },
        ]}
        meta={m.console_store_channel()}
        name={tenant.storeName}
      />
    );

  return (
    <div className="fixed inset-0 flex bg-[var(--bg-page-body)]">
      {/* 侧栏自己是 sticky + flex-none，外面这层只负责在窄屏把它整块收掉。 */}
      <div className="hidden flex-none md:block">
        {/* logo 是 <img>，外面的 CSS 管不到里面的填充色，所以暗色字色只能整份换文件；
            两份 SVG 只差字色，粉色标记两边一样。 */}
        <SideRail
          footer={
            <div className="flex flex-col gap-0.5">
              <NavItemLink
                active={settingsItem.to === pathname}
                icon={
                  <NavIcon
                    active={settingsItem.to === pathname}
                    name={settingsItem.icon}
                  />
                }
                label={settingsItem.label}
                to={settingsItem.to}
                search={{ tab: "appearance" }}
              />
            </div>
          }
          logoSrc={isDark ? logoDarkUrl : logoUrl}
          store={storeSwitcher}
        >
          {navItems.map((item) => (
            <NavItemLink
              active={item.to === pathname}
              icon={<NavIcon active={item.to === pathname} name={item.icon} />}
              key={item.to}
              label={item.label}
              to={item.to}
            />
          ))}
        </SideRail>
      </div>
      <main className="flex min-h-0 min-w-0 flex-1 flex-col pb-[var(--shopify-safe-area-inset-bottom,0px)]">
        {/* 页面按内容区自己的宽度做响应式，不看视口：侧栏占掉的宽度不进计算。
            窄屏下方留出底部导航的高度，最后一行不被压住。 */}
        <ScrollArea
          height="100%"
          className="flex-1"
          viewportClassName="p-4 pb-24 md:p-7"
          contentClassName="@container flex h-full min-h-0 flex-col"
        >
          {/* 窄屏把店铺菜单移到顶部，主题切换仍在同一个菜单里。 */}
          <div className="mb-4 shrink-0 md:hidden">{storeSwitcher}</div>
          <div className="min-h-0 flex-1">{children}</div>
        </ScrollArea>
      </main>
      {/* 浮在内容之上、弹层之下：@go-novus/ui 的弹层从 z-940 起，菜单和抽屉要能盖住它。 */}
      <div className="fixed inset-x-3 bottom-[calc(var(--spacing)*3+var(--shopify-safe-area-inset-bottom,0px))] z-[130] md:hidden">
        <MobileNav
          items={allItems.map((item) => ({
            icon: <NavIcon active={item.to === pathname} name={item.icon} />,
            label: item.label,
            value: item.to,
          }))}
          onChange={(value) => {
            const next = allItems.find((item) => item.to === value);

            if (next !== undefined) {
              void navigate({
                to: next.to,
                search: next.to === "/settings" ? { tab: "appearance" } : {},
              });
            }
          }}
          value={activeItem?.to}
        />
      </div>
    </div>
  );
}
